<template>
  <v-container fluid class="mt-16 px-6">
    <div class="mb-2 d-flex flex-wrap align-center" style="gap: 4px">
      <p class="subtitle-2 mb-0 mr-1">Actions:</p>

      <print-batch-item-modal />

      <!-- all items list modal -->
      <v-btn @click="enable_all_item_modal = true" small text
        ><v-icon class="mr-2">mdi-box</v-icon>All Items List</v-btn
      >
      <item-types-modal class="mr-2" v-if="can('items.item_types')" />

      <v-btn
        color="yellow"
        class="mr-2"
        @click="print_inventory"
        v-if="can('items.print_inventory')"
        >Print Inventory</v-btn
      >
      <item-transfer-modal v-if="can('items.transfer')" />
      <consume-items-modal v-if="can('items.consume')" class="ml-2" />
      <v-btn
        small
        text
        :color="warranty_alerts.length > 0 ? 'red' : 'grey'"
        @click="openWarrantyAlerts"
        :loading="warrantyAlertsLoading"
      >
        <v-icon class="mr-1" small>mdi-shield-alert</v-icon>
        Warranty Alerts
        <v-chip
          v-if="warranty_alerts.length > 0"
          x-small
          color="red"
          dark
          class="ml-1"
        >
          {{ warranty_alerts.length }}
        </v-chip>
      </v-btn>
      <v-btn
        v-if="can('items.category_price_adjustment')"
        small
        text
        color="purple"
        @click="categoryAdjDialog = true"
      >
        <v-icon class="mr-1" small>mdi-tag-multiple</v-icon>
        Category Price Adjustment
      </v-btn>
    </div>
    <v-divider></v-divider>
    <div v-if="ready">
      <!-- ITEMS TABLE  -->
      <v-card class="pl-5 pr-5" flat>
        <v-card-title class="d-flex">
          <h2 v-if="!isPhone" class="text-uppercase">Inventory Management</h2>
          <v-spacer></v-spacer>
          <AddItemModal
            v-if="can('items.add')"
            ref="addItemModal"
            :hide-trigger="isPhone"
          />
          <v-btn
            color=""
            class=""
            icon
            x-large
            @click="refresh"
            :loading="loading"
            ><v-icon>mdi-refresh</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="item_search"
            prepend-icon="mdi-magnify"
            label="Search Item"
          ></v-text-field>
          <!-- Bulk reorder point toolbar -->
          <v-toolbar
            v-if="selected_items.length > 0"
            dense
            flat
            color="blue lighten-5"
            class="mb-3 rounded"
          >
            <v-icon class="mr-2">mdi-checkbox-multiple-marked</v-icon>
            <span class="subtitle-2"
              >{{ selected_items.length }} item(s) selected</span
            >
            <v-spacer></v-spacer>
            <v-btn color="primary" small @click="bulk_reorder_dialog = true">
              <v-icon left small>mdi-alert-circle-outline</v-icon>
              Set Reorder Point & Min Stock
            </v-btn>
            <v-btn
              v-if="can('items.merge') && selected_items.length === 2"
              color="orange"
              small
              dark
              class="ml-2"
              @click="open_merge_dialog"
            >
              <v-icon left small>mdi-merge</v-icon>
              Merge Selected
            </v-btn>
            <v-btn
              v-if="can('items.delete')"
              color="red"
              small
              dark
              class="ml-2"
              @click="confirm_bulk_delete"
              :loading="bulk_deleting"
            >
              <v-icon left small>mdi-delete</v-icon>
              Delete Selected
            </v-btn>
            <v-btn text small class="ml-2" @click="selected_items = []">
              Clear Selection
            </v-btn>
          </v-toolbar>
          <!-- Mobile: card list -->
          <div v-if="isPhone">
            <mobile-record-card
              v-for="item in $mobileSearch(items, item_search)"
              :key="item.id"
              icon="mdi-package-variant"
              :title="item.item_name"
              :subtitle="
                (item.latest_lot ? item.latest_lot.supplier : item.supplier) ||
                ''
              "
              :fields="[
                {
                  label: 'Price',
                  value:
                    '₱' +
                    Number(
                      item.latest_lot
                        ? item.latest_lot.unit_cost
                        : item.unit_price
                    ).toLocaleString(),
                },
                {
                  label: 'VAT',
                  value: (
                    item.latest_lot ? item.latest_lot.is_vat : item.is_vat
                  )
                    ? 'VAT'
                    : 'Non-VAT',
                },
                { label: 'Class', value: item.part_classification },
              ]"
            >
              <template #chip>
                <v-chip
                  small
                  :color="item.quantity >= 1 ? 'green' : 'red'"
                  class="white--text"
                  >{{ item.quantity }}</v-chip
                >
              </template>
              <template #actions>
                <v-btn
                  v-if="hasPermission('items.view_profile')"
                  icon
                  color="primary"
                  @click="open_item_profile(item)"
                  ><v-icon>mdi-list-box</v-icon></v-btn
                >
                <v-btn
                  v-if="can('items.edit')"
                  icon
                  color="amber darken-2"
                  @click="edit_item(item)"
                  ><v-icon>mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="can('items.restock')"
                  icon
                  color="teal"
                  @click="openRestock(item)"
                  ><v-icon>mdi-plus-box</v-icon></v-btn
                >
                <v-btn
                  v-if="can('items.delete')"
                  icon
                  color="red"
                  :loading="loading"
                  @click="confirm_delete(item)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </template>
            </mobile-record-card>
            <div
              v-if="!$mobileSearch(items, item_search).length"
              class="text-center text--secondary py-8"
            >
              No items found.
            </div>
          </div>
          <!-- Desktop: data table -->
          <v-data-table
            v-else
            :items="items"
            :headers="item_headers"
            :search="item_search"
            show-select
            v-model="selected_items"
          >
            <template v-slot:item.quantity="{ item }">
              <v-chip
                v-if="item.quantity >= 1"
                color="green"
                class="white--text"
                >{{ item.quantity }}</v-chip
              >
              <v-chip v-else color="red" class="white--text">
                {{ item.quantity }}
              </v-chip>
            </template>
            <template v-slot:item.item_name="{ item }">
              <div class="d-flex align-center">
                <span
                  :class="
                    (item.latest_lot ? item.latest_lot.is_vat : item.is_vat)
                      ? 'green--text font-weight-bold'
                      : 'orange--text font-weight-bold'
                  "
                  >{{ item.item_name }}</span
                >
                <v-tooltip top v-if="item.is_serialized">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="blue"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-barcode</v-icon
                    >
                  </template>
                  <span>Serialized item — serials required on stock-in</span>
                </v-tooltip>
              </div>
            </template>
            <template v-slot:item.unit_price="{ item }">
              <span v-if="item.latest_lot">
                ₱{{ Number(item.latest_lot.unit_cost).toLocaleString() }}
              </span>
              <span v-else>
                ₱{{ Number(item.unit_price).toLocaleString() }}
              </span>
            </template>
            <template v-slot:item.supplier="{ item }">
              <span v-if="item.latest_lot">{{
                item.latest_lot.supplier || "-"
              }}</span>
              <span v-else>{{ item.supplier || "-" }}</span>
            </template>
            <template v-slot:item.shipping_cost="{ item }">
              <span v-if="item.latest_lot">
                ₱{{
                  Number(item.latest_lot.shipping_cost || 0).toLocaleString()
                }}
              </span>
              <span v-else>-</span>
            </template>
            <template v-slot:item.is_vat="{ item }">
              <v-chip
                x-small
                :color="
                  (item.latest_lot ? item.latest_lot.is_vat : item.is_vat)
                    ? 'green'
                    : 'orange'
                "
                dark
              >
                {{
                  (item.latest_lot ? item.latest_lot.is_vat : item.is_vat)
                    ? "VAT"
                    : "Non-VAT"
                }}
              </v-chip>
            </template>
            <template v-slot:item.part_classification="{ item }">
              <v-chip
                v-if="item.part_classification === 'major'"
                small
                color="blue"
                dark
                >Major</v-chip
              >
              <v-chip
                v-else-if="item.part_classification === 'minor'"
                small
                color="orange"
                dark
                >Minor</v-chip
              >
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.warranty="{ item }">
              <span v-if="item.warranty">{{
                item.warranty.warranty_count +
                " " +
                item.warranty.warranty_duration
              }}</span>
            </template>

            <template v-slot:item.action="{ item }">
              <div class="d-flex">
                <v-btn
                  v-if="hasPermission('items.view_profile')"
                  icon
                  color="primary"
                  @click="open_item_profile(item)"
                  ><v-icon>mdi-list-box</v-icon></v-btn
                >
                <v-btn
                  v-if="can('items.edit')"
                  @click="edit_item(item)"
                  icon
                  color="yellow"
                  ><v-icon>mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="can('items.restock')"
                  icon
                  color="teal"
                  @click="openRestock(item)"
                  ><v-icon>mdi-plus-box</v-icon></v-btn
                >
                <!-- <edit-resellers-item-modal :item="item" /> -->
                <v-btn
                  v-if="can('items.delete')"
                  icon
                  color="red"
                  @click="confirm_delete(item)"
                  :loading="loading"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </template>
          </v-data-table></v-card-text
        >
      </v-card>
      <v-divider></v-divider>
    </div>
    <v-row v-else class="d-flex justify-center">
      <h2 class="text-center mt-10 mb-10">Data Table is loading...</h2>
    </v-row>
    <!-- Lazy loaded components -->
    <item-profile-modal
      :item="selected_item"
      v-if="item_profile_modal"
      @close="item_profile_modal = false"
    />
    <edit-item-modal
      :item="selected_item"
      v-if="edit_item_modal"
      @close="edit_item_modal = false"
    />
    <all-item-modal
      v-if="enable_all_item_modal"
      @close="enable_all_item_modal = false"
    />
    <!-- lazy loaded components -->
    <!-- Restock Dialog -->
    <v-dialog v-model="restockDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="green white--text">
          <v-icon left color="white">mdi-plus-box</v-icon>
          Add Stock to Existing Item
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            <strong>{{ restockItem.item_name }}</strong
            ><br />
            Current Stock: <strong>{{ restockItem.quantity }}</strong> | Branch:
            <strong>{{
              restockItem.branch ? restockItem.branch.branch_name : ""
            }}</strong>
          </v-alert>

          <v-text-field
            type="number"
            label="Quantity *"
            v-model="restockForm.quantity"
            outlined
            dense
            prepend-inner-icon="mdi-package-variant-closed-plus"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Unit Cost *"
            v-model="restockForm.unit_cost"
            outlined
            dense
            prepend-inner-icon="mdi-cash"
            prefix="₱"
          ></v-text-field>
          <v-autocomplete
            label="Supplier"
            v-model="restockForm.supplier"
            :items="suppliers"
            item-text="supplier_name"
            item-value="supplier_name"
            outlined
            dense
            prepend-inner-icon="mdi-truck"
          ></v-autocomplete>
          <v-text-field
            label="Purchase Date"
            v-model="restockForm.purchase_date"
            type="date"
            outlined
            dense
            prepend-inner-icon="mdi-calendar"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Shipping Cost"
            v-model="restockForm.shipping_cost"
            outlined
            dense
            prepend-inner-icon="mdi-truck-delivery"
            prefix="₱"
          ></v-text-field>
          <v-radio-group v-model="restockForm.is_vat" row>
            <template v-slot:label>
              <div class="text-body-2 font-weight-bold">Tax Option</div>
            </template>
            <v-radio label="Non-VAT" :value="false"></v-radio>
            <v-radio label="VAT" :value="true"></v-radio>
          </v-radio-group>
          <div class="d-flex align-center">
            <v-text-field
              type="number"
              label="Supplier Warranty"
              v-model="restockForm.supplier_warranty_months"
              outlined
              dense
              prepend-inner-icon="mdi-shield-check"
              class="flex-grow-1 mr-2"
            ></v-text-field>
            <v-select
              v-model="restockForm.supplier_warranty_unit"
              :items="warrantyUnits"
              outlined
              dense
              style="max-width: 140px"
            ></v-select>
          </div>
          <div class="d-flex mb-4">
            <v-chip
              v-for="preset in warrantyPresets[
                restockForm.supplier_warranty_unit || 'months'
              ]"
              :key="preset"
              small
              class="mr-2"
              :color="
                restockForm.supplier_warranty_months == preset
                  ? 'primary'
                  : 'grey lighten-2'
              "
              :dark="restockForm.supplier_warranty_months == preset"
              @click="restockForm.supplier_warranty_months = preset"
            >
              {{ preset
              }}{{
                warrantyUnitShort[
                  restockForm.supplier_warranty_unit || "months"
                ]
              }}
            </v-chip>
          </div>
          <div class="d-flex align-center mb-4">
            <v-select
              v-model="restockForm.import_batch_id"
              label="Batch Import"
              :items="import_batches"
              item-value="id"
              item-text="batch_description"
              outlined
              dense
              hide-details
              class="flex-grow-1"
              prepend-inner-icon="mdi-package-variant"
            ></v-select>
            <add-batch-import-modal
              class="ml-2"
              :preset-supplier-id="restockSupplierId"
              @added="onBatchAdded"
            />
          </div>
          <v-text-field
            label="Remarks"
            v-model="restockForm.remarks"
            outlined
            dense
            prepend-inner-icon="mdi-note-text"
          ></v-text-field>
          <v-checkbox
            v-if="!restockItem.is_serialized"
            v-model="restockForm.mark_serialized"
            color="primary"
            dense
            hide-details
            class="mt-0 mb-1"
          >
            <template v-slot:label>
              <span>
                <v-icon small color="primary" class="mr-1">mdi-barcode</v-icon>
                Serialized item — capture a serial per unit
              </span>
            </template>
          </v-checkbox>
          <div
            v-if="!restockItem.is_serialized && restockForm.mark_serialized"
            class="caption orange--text mb-2"
          >
            This marks the item serialized. Existing stock ({{
              restockItem.quantity
            }}) stays un-serialized until you backfill it on the item's Serial
            Numbers tab.
          </div>
          <serial-numbers-input
            v-if="restockItem.is_serialized || restockForm.mark_serialized"
            v-model="restockForm.serials_text"
            :quantity="restockForm.quantity"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockDialog = false">Cancel</v-btn>
          <v-btn
            color="green"
            dark
            @click="submitRestock"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Add Stock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Reorder Point Dialog -->
    <v-dialog v-model="bulk_reorder_dialog" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>
          Set Reorder Point & Min Stock Level
        </v-card-title>
        <v-card-subtitle class="mt-1">
          Applying to {{ selected_items.length }} selected item(s)
        </v-card-subtitle>
        <v-card-text>
          <v-text-field
            prepend-icon="mdi-alert-circle-outline"
            type="number"
            label="Reorder Point"
            v-model.number="bulk_reorder_point"
            hint="Alert when stock falls below this level"
            persistent-hint
            min="0"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            prepend-icon="mdi-package-variant-closed-minus"
            type="number"
            label="Min Stock Level"
            v-model.number="bulk_min_stock_level"
            hint="Minimum stock to maintain"
            persistent-hint
            min="0"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeBulkReorderDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveBulkReorderPoints"
            :loading="bulk_saving"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Merge Items Dialog -->
    <v-dialog v-model="merge_dialog" max-width="700" persistent>
      <v-card v-if="merge_items_pair.length === 2">
        <v-card-title class="orange white--text">
          <v-icon left color="white">mdi-merge</v-icon>
          Merge Duplicate Items
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" dense outlined class="mb-4">
            This will move all stock batches, sales history, and references from
            the
            <strong>source</strong> item into the <strong>target</strong> item,
            then delete the source. This action cannot be undone.
          </v-alert>
          <p class="subtitle-2 mb-3">
            Select which item to <strong>keep</strong> (target):
          </p>
          <v-radio-group v-model="merge_target_id" mandatory>
            <v-card
              v-for="item in merge_items_pair"
              :key="item.id"
              outlined
              class="mb-3 pa-3"
              :class="merge_target_id === item.id ? 'orange lighten-5' : ''"
              @click="merge_target_id = item.id"
            >
              <v-radio :value="item.id" class="mb-0">
                <template v-slot:label>
                  <div class="ml-2" style="width: 100%">
                    <div class="d-flex align-center">
                      <strong>{{ item.item_name }}</strong>
                      <v-chip
                        x-small
                        class="ml-2"
                        :color="merge_target_id === item.id ? 'orange' : 'grey'"
                        dark
                      >
                        {{
                          merge_target_id === item.id
                            ? "KEEP"
                            : "MERGE INTO TARGET"
                        }}
                      </v-chip>
                    </div>
                    <div class="caption grey--text mt-1">
                      ID: {{ item.id }} | Stock: {{ item.quantity }} | Type:
                      {{ item.item_type ? item.item_type.item_type : "N/A"
                      }}{{
                        item.item_sub_type
                          ? " / " + item.item_sub_type.item_sub_type
                          : ""
                      }}
                      | Supplier: {{ item.supplier || "N/A" }} | Unit Price: ₱{{
                        Number(item.unit_price).toLocaleString()
                      }}
                      | Selling Price: ₱{{
                        Number(item.selling_price).toLocaleString()
                      }}
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-card>
          </v-radio-group>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="merge_dialog = false">Cancel</v-btn>
          <v-btn
            color="orange"
            dark
            @click="confirm_merge"
            :loading="merge_loading"
          >
            <v-icon left>mdi-merge</v-icon>
            Merge Items
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Warranty Alerts Dialog -->
    <v-dialog v-model="warrantyAlertsDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="red white--text">
          <v-icon left color="white">mdi-shield-alert</v-icon>
          Supplier Warranty Alerts
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 500px">
          <v-alert
            v-if="warranty_alerts.length === 0"
            type="success"
            dense
            outlined
          >
            No warranty alerts. All stock batches are within warranty coverage.
          </v-alert>
          <div v-else>
            <v-alert type="warning" dense outlined class="mb-3">
              {{ warranty_alerts.length }} item(s) have batches with expiring or
              expired supplier warranties.
            </v-alert>
            <v-data-table
              :items="warranty_alerts"
              :headers="warrantyAlertHeaders"
              dense
              :items-per-page="10"
            >
              <template v-slot:item.warranty_status="{ item }">
                <v-chip
                  :color="
                    item.warranty_status === 'expired'
                      ? 'red'
                      : 'amber darken-2'
                  "
                  dark
                  x-small
                >
                  {{
                    item.warranty_status === "expired" ? "Expired" : "Expiring"
                  }}
                </v-chip>
              </template>
              <template v-slot:item.lots="{ item }">
                <div v-for="lot in item.lots" :key="lot.id" class="mb-1">
                  <v-chip x-small class="mr-1">Batch #{{ lot.id }}</v-chip>
                  <span class="caption">
                    {{ lot.supplier || "N/A" }} —
                    {{ lot.remaining_quantity }} units —
                    <span
                      :class="
                        lot.warranty_status === 'expired'
                          ? 'red--text'
                          : 'amber--text text--darken-3'
                      "
                    >
                      {{
                        lot.warranty_days_remaining != null
                          ? lot.warranty_days_remaining >= 0
                            ? lot.warranty_days_remaining + "d left"
                            : "expired"
                          : "N/A"
                      }}
                    </span>
                  </span>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="warrantyAlertsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Restock Price Confirmation Dialog -->
    <v-dialog v-model="restockPriceDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="orange white--text">
          <v-icon left color="white">mdi-cash-sync</v-icon>
          Update Selling Price?
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            The new unit cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) differs from the current unit price (₱{{
              Number(restockItem.unit_price).toLocaleString()
            }}).
          </v-alert>
          <p class="subtitle-2 mb-3">
            Would you like to update the selling price?
          </p>
          <v-radio-group v-model="restockPriceOption" column>
            <v-radio
              value="auto"
              :label="`Accept auto-calculated: ₱${Number(
                restockAutoPrice
              ).toLocaleString()}`"
            ></v-radio>
            <v-radio value="custom" label="Set custom price"></v-radio>
            <v-radio
              value="skip"
              label="Skip — keep current selling price"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="restockPriceOption === 'custom'"
            type="number"
            label="Custom Selling Price"
            v-model="restockCustomPrice"
            outlined
            dense
            prefix="₱"
            class="mt-2"
          ></v-text-field>
          <div
            class="caption grey--text mt-2"
            v-if="restockPriceOption === 'auto'"
          >
            Calculated: Unit Cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) + {{ restockItem.profitable_margin || 0 }}% margin + ₱{{
              Number(restockForm.shipping_cost || 0).toLocaleString()
            }}
            shipping
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockPriceDialog = false">Cancel</v-btn>
          <v-btn
            color="orange"
            dark
            @click="confirmRestockPrice"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Confirm & Restock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Category Price Adjustment Dialog -->
    <v-dialog v-model="categoryAdjDialog" persistent max-width="800" scrollable>
      <v-card>
        <v-card-title class="purple white--text">
          <v-icon left color="white">mdi-tag-multiple</v-icon>
          Category Price Adjustment
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 600px">
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                label="Category (Item Type) *"
                v-model="categoryAdjForm.item_type_id"
                :items="item_types"
                item-text="item_type"
                item-value="id"
                outlined
                dense
                prepend-inner-icon="mdi-shape"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                label="Adjustment Type *"
                v-model="categoryAdjForm.adjustment_type"
                :items="adjustmentTypes"
                item-text="text"
                item-value="value"
                outlined
                dense
                prepend-inner-icon="mdi-tune"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                type="number"
                :label="
                  categoryAdjForm.adjustment_type &&
                  categoryAdjForm.adjustment_type.includes('percentage')
                    ? 'Adjustment Value (%) *'
                    : 'Adjustment Value (₱) *'
                "
                v-model="categoryAdjForm.adjustment_value"
                outlined
                dense
                prepend-inner-icon="mdi-numeric"
                :prefix="
                  categoryAdjForm.adjustment_type &&
                  categoryAdjForm.adjustment_type.includes('percentage')
                    ? '%'
                    : '₱'
                "
                min="0"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Reason (optional)"
                v-model="categoryAdjForm.reason"
                outlined
                dense
                prepend-inner-icon="mdi-note-text"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-btn
            color="blue"
            outlined
            @click="previewCategoryAdj"
            :loading="categoryAdjPreviewLoading"
            :disabled="
              !categoryAdjForm.item_type ||
              !categoryAdjForm.adjustment_type ||
              !categoryAdjForm.adjustment_value
            "
          >
            <v-icon left>mdi-eye</v-icon>Preview Changes
          </v-btn>

          <!-- Preview Table -->
          <div
            v-if="
              categoryAdjPreview &&
              categoryAdjPreview.items &&
              categoryAdjPreview.items.length > 0
            "
            class="mt-4"
          >
            <v-alert type="info" dense outlined class="mb-3">
              {{ categoryAdjPreview.count }} item(s) will be affected.
            </v-alert>
            <v-data-table
              :items="categoryAdjPreview.items"
              :headers="categoryAdjPreviewHeaders"
              dense
              :items-per-page="10"
            >
              <template v-slot:item.current_price="{ item }">
                ₱{{ Number(item.current_price).toLocaleString() }}
              </template>
              <template v-slot:item.new_price="{ item }">
                <span
                  class="font-weight-bold"
                  :class="item.difference >= 0 ? 'green--text' : 'red--text'"
                >
                  ₱{{ Number(item.new_price).toLocaleString() }}
                </span>
              </template>
              <template v-slot:item.difference="{ item }">
                <span
                  :class="item.difference >= 0 ? 'green--text' : 'red--text'"
                >
                  {{ item.difference >= 0 ? "+" : "" }}₱{{
                    Number(item.difference).toLocaleString()
                  }}
                </span>
              </template>
            </v-data-table>
          </div>
          <v-alert
            v-if="
              categoryAdjPreview &&
              categoryAdjPreview.items &&
              categoryAdjPreview.items.length === 0
            "
            type="warning"
            dense
            outlined
            class="mt-4"
          >
            No items found for this category in your branch.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeCategoryAdjDialog">Cancel</v-btn>
          <v-btn
            color="purple"
            dark
            @click="applyCategoryAdj"
            :loading="categoryAdjApplyLoading"
            :disabled="
              !categoryAdjPreview ||
              !categoryAdjPreview.items ||
              categoryAdjPreview.items.length === 0
            "
          >
            <v-icon left>mdi-check-all</v-icon>Apply Adjustment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mobile: floating Add Item -->
    <v-btn
      v-if="isPhone && ready && can('items.add')"
      fab
      fixed
      bottom
      right
      color="primary"
      class="mobile-fab"
      @click="$refs.addItemModal.openDialog()"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AddItemModal from "../components/Item/addItemModal.vue";
import ConsumeItemsModal from "../components/Item/consumeItemsModal.vue";
import ItemTransferModal from "../components/Item/itemTransferModal.vue";
import ItemTypesModal from "../components/Item/itemTypesModal.vue";
import PrintBatchItemModal from "../components/Item/printBatchItemModal.vue";
import AddBatchImportModal from "../components/BatchImport/addBatchImportModal.vue";
import SerialNumbersInput from "../components/Item/serialNumbersInput.vue";
import { parseSerials, serialError } from "../utils/serialNumbers";

export default {
  data() {
    return {
      enable_all_item_modal: false,
      selected_item: [],
      item_profile_modal: false,
      edit_item_modal: false,
      item_search: "",
      loading: false,
      ready: false,

      item_headers: [
        { text: "Actions", value: "action" },
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Minimum Quantity",
          value: "min_stock_level",
        },
        {
          text: "Re-order Point",
          value: "reorder_point",
        },
        {
          text: "Available Stocks",
          value: "quantity",
        },
        {
          text: "Item Name",
          value: "item_name",
          width: "250",
        },
        {
          text: "Unit Price (Latest Batch)",
          value: "unit_price",
        },
        {
          text: "Selling Price",
          value: "selling_price",
        },
        {
          text: "Supplier (Latest Batch)",
          value: "supplier",
        },
        {
          text: "Shipping (Latest Batch)",
          value: "shipping_cost",
        },
        {
          text: "VAT (Latest Batch)",
          value: "is_vat",
        },
        {
          text: "Item Type",
          value: "item_type.item_type",
        },
        {
          text: "Sub Type",
          value: "item_sub_type.item_sub_type",
        },
        {
          text: "Classification",
          value: "part_classification",
        },
        {
          text: "Unit",
          value: "unit",
        },
        {
          text: "Warranty",
          value: "warranty",
        },
      ],
      selected_items: [],
      bulk_reorder_dialog: false,
      bulk_reorder_point: 0,
      bulk_min_stock_level: 0,
      bulk_saving: false,
      bulk_deleting: false,
      restockDialog: false,
      restockLoading: false,
      restockItem: null,
      restockForm: {
        quantity: "",
        unit_cost: "",
        supplier: "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: 0,
        is_vat: false,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        remarks: "",
      },
      warrantyUnits: [
        { text: "Days", value: "days" },
        { text: "Weeks", value: "weeks" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],
      warrantyPresets: {
        days: [30, 60, 90, 180],
        weeks: [4, 12, 26, 52],
        months: [3, 6, 12, 24],
        years: [1, 2, 3, 5],
      },
      warrantyUnitShort: {
        days: "d",
        weeks: "w",
        months: "mo",
        years: "yr",
      },
      merge_dialog: false,
      merge_loading: false,
      merge_items_pair: [],
      merge_target_id: null,
      // Restock price confirmation
      restockPriceDialog: false,
      restockPriceOption: "auto",
      restockCustomPrice: "",
      restockAutoPrice: 0,
      // Category price adjustment
      categoryAdjDialog: false,
      categoryAdjPreviewLoading: false,
      categoryAdjApplyLoading: false,
      categoryAdjPreview: null,
      categoryAdjForm: {
        item_type_id: "",
        adjustment_type: "",
        adjustment_value: "",
        reason: "",
      },
      adjustmentTypes: [
        { text: "Percentage Increase", value: "percentage_increase" },
        { text: "Percentage Decrease", value: "percentage_decrease" },
        { text: "Fixed Increase (₱)", value: "fixed_increase" },
        { text: "Fixed Decrease (₱)", value: "fixed_decrease" },
      ],
      categoryAdjPreviewHeaders: [
        { text: "Item Name", value: "item_name" },
        { text: "Current Price", value: "current_price" },
        { text: "New Price", value: "new_price" },
        { text: "Difference", value: "difference" },
      ],
      warrantyAlertsDialog: false,
      warrantyAlertsLoading: false,
      warrantyAlertHeaders: [
        { text: "Item", value: "item_name" },
        { text: "Type", value: "item_type" },
        { text: "Status", value: "warranty_status", sortable: false },
        { text: "At-Risk Units", value: "total_at_risk" },
        { text: "Earliest Expiry", value: "earliest_expiry" },
        { text: "Batch Details", value: "lots", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
      items: "item/items",
      default_server_url: "auth/default_server_url",
      item_types: "item_type/item_types",
      suppliers: "supplier/suppliers",
      branches: "branch/branches",
      import_batches: "importBatch/import_batches",
      warranty_alerts: "item/warranty_alerts",
    }),
    latest_import_batch_id() {
      if (!this.import_batches || this.import_batches.length === 0) return "";
      return this.import_batches.reduce((latest, b) =>
        Number(b.id) > Number(latest.id) ? b : latest
      ).id;
    },
    // Resolve the supplier chosen in the restock form (stored as a name) to its
    // id, so the Add Batch Import dialog can pre-select it and auto-fill the
    // batch tag. (JMB-18)
    restockSupplierId() {
      if (!this.restockForm.supplier || !this.suppliers) return null;
      const match = this.suppliers.find(
        (s) => s.supplier_name === this.restockForm.supplier
      );
      return match ? match.id : null;
    },
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    open_item_profile(item) {
      this.selected_item = item;
      this.item_profile_modal = true;
    },
    edit_item(item) {
      this.selected_item = item;
      this.edit_item_modal = true;
    },
    async print_inventory() {
      let display_price = await this.$confirm({
        title: "Print Inventory",
        message: "Display price?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      display_price = display_price ? 1 : 0;
      let url =
        this.default_server_url +
        "/print_inventory/" +
        this.user.branch_id +
        "/" +
        display_price;
      window.open(url);
    },
    async confirm_delete(item) {
      let x = await this.$confirm({
        title: "Delete Item",
        message: "Are you sure you want to delete this item?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.loading = true;
        this.delete_item(item.id).then(() => {
          this.$alert({
            title: "Success",
            message: "Successfully deleted an item",
            color: "success",
            icon: "mdi-check-circle",
          });
          this.loading = false;
        });
      }
    },
    async confirm_bulk_delete() {
      let x = await this.$confirm({
        title: "Bulk Delete Items",
        message:
          "Are you sure you want to delete " +
          this.selected_items.length +
          " item(s)? This action can be reversed from the All Items List.",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.bulk_deleting = true;
        const ids = this.selected_items.map((item) => item.id);
        this.bulk_delete_items(ids)
          .then(() => {
            this.$alert({
              title: "Success",
              message: "Successfully deleted " + ids.length + " item(s)",
              color: "success",
              icon: "mdi-check-circle",
            });
            this.selected_items = [];
          })
          .catch(() => {
            this.$alert({
              title: "Error",
              message: "Failed to delete items. Please try again.",
              color: "error",
              icon: "mdi-alert-circle",
            });
          })
          .finally(() => {
            this.bulk_deleting = false;
          });
      }
    },
    ...mapActions({
      get_items: "item/get_items",
      get_branches: "branch/get_branches",
      delete_item: "item/delete_item",
      bulk_delete_items: "item/bulk_delete_items",
      get_item_types: "item_type/get_item_types",
      get_suppliers: "supplier/get_suppliers",
      bulk_update_reorder_points: "stock_alert/bulk_update_reorder_points",
      restock_item: "item/restock_item",
      get_import_batches: "importBatch/get_import_batches",
      merge_items_action: "item/merge_items",
      get_warranty_alerts: "item/get_warranty_alerts",
      category_price_adjustment_preview:
        "item/category_price_adjustment_preview",
      category_price_adjustment: "item/category_price_adjustment",
    }),
    async openWarrantyAlerts() {
      this.warrantyAlertsLoading = true;
      try {
        await this.get_warranty_alerts({
          branchId: this.user.branch_id,
          days: 90,
        });
      } catch (err) {
        console.error("Failed to load warranty alerts:", err);
      }
      this.warrantyAlertsLoading = false;
      this.warrantyAlertsDialog = true;
    },
    open_merge_dialog() {
      if (this.selected_items.length !== 2) return;
      this.merge_items_pair = [...this.selected_items];
      this.merge_target_id = this.merge_items_pair[0].id;
      this.merge_dialog = true;
    },
    async confirm_merge() {
      const target = this.merge_items_pair.find(
        (i) => i.id === this.merge_target_id
      );
      const source = this.merge_items_pair.find(
        (i) => i.id !== this.merge_target_id
      );
      if (!target || !source) return;

      let confirmed = await this.$confirm({
        title: "Merge Items",
        message:
          `Are you sure you want to merge "${source.item_name}" (ID: ${source.id}) into "${target.item_name}" (ID: ${target.id})?<br><br>` +
          `All stock, sales history, and references from item #${source.id} will be moved to item #${target.id}.<br>` +
          `Item #${source.id} will be deleted.<br><br>This cannot be undone.`,
        color: "orange",
        icon: "mdi-alert",
      });
      if (!confirmed) return;

      this.merge_loading = true;
      try {
        await this.merge_items_action({
          target_item_id: target.id,
          source_item_id: source.id,
        });
        this.$alert({
          title: "Success",
          message: `Successfully merged item #${source.id} into item #${target.id}.`,
          color: "success",
          icon: "mdi-check-circle",
        });
        this.merge_dialog = false;
        this.selected_items = [];
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.merge_loading = false;
      }
    },
    async openRestock(item) {
      this.restockItem = item;
      const lot = item.latest_lot;
      this.restockForm = {
        quantity: "",
        unit_cost: lot ? lot.unit_cost : item.unit_price || "",
        supplier: lot ? lot.supplier || "" : item.supplier || "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: lot ? lot.shipping_cost || 0 : 0,
        is_vat: lot ? !!lot.is_vat : !!item.is_vat,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        remarks: "",
        serials_text: "",
        mark_serialized: false,
      };
      this.restockDialog = true;
      if (this.import_batches.length <= 0) {
        await this.get_import_batches();
      }
      // Default to the latest imported batch.
      this.restockForm.import_batch_id = this.latest_import_batch_id;
    },
    onBatchAdded(batch) {
      if (batch && batch.id) {
        this.restockForm.import_batch_id = batch.id;
      }
    },
    async submitRestock() {
      if (!this.restockForm.quantity || this.restockForm.quantity <= 0) {
        this.$alert({
          title: "Validation",
          message: "Please enter a quantity greater than 0",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.restockForm.unit_cost) {
        this.$alert({
          title: "Validation",
          message: "Please enter a unit cost",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.restockItem.is_serialized || this.restockForm.mark_serialized) {
        const err = serialError(
          parseSerials(this.restockForm.serials_text),
          this.restockForm.quantity
        );
        if (err) {
          this.$alert({
            title: "Serial Numbers",
            message: err,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }
      // Check if unit cost differs from item's current unit_price
      const newCost = parseFloat(this.restockForm.unit_cost);
      const oldCost = parseFloat(this.restockItem.unit_price);
      const margin = parseFloat(this.restockItem.profitable_margin) || 0;

      if (newCost !== oldCost && margin > 0) {
        // Calculate auto price: unit_cost + (unit_cost * margin / 100) + shipping_cost
        const shipping = parseFloat(this.restockForm.shipping_cost) || 0;
        this.restockAutoPrice = Math.round(
          newCost + (newCost * margin) / 100 + shipping
        );
        this.restockPriceOption = "auto";
        this.restockCustomPrice = "";
        this.restockDialog = false;
        this.restockPriceDialog = true;
      } else {
        await this.doRestock(false);
      }
    },
    async confirmRestockPrice() {
      if (this.restockPriceOption === "skip") {
        await this.doRestock(false);
      } else if (this.restockPriceOption === "auto") {
        await this.doRestock(
          true,
          this.restockForm.unit_cost,
          this.restockAutoPrice
        );
      } else if (this.restockPriceOption === "custom") {
        if (!this.restockCustomPrice || this.restockCustomPrice <= 0) {
          this.$alert({
            title: "Validation",
            message: "Please enter a valid custom selling price",
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }
        await this.doRestock(
          true,
          this.restockForm.unit_cost,
          this.restockCustomPrice
        );
      }
    },
    async doRestock(updatePrice, newUnitPrice, newSellingPrice) {
      this.restockLoading = true;
      try {
        const payload = { ...this.restockForm };
        delete payload.serials_text;
        if (
          this.restockItem.is_serialized ||
          this.restockForm.mark_serialized
        ) {
          payload.serials = parseSerials(this.restockForm.serials_text);
        }
        if (updatePrice) {
          payload.update_selling_price = true;
          payload.new_unit_price = newUnitPrice;
          payload.new_selling_price = newSellingPrice;
        }
        await this.restock_item({
          itemId: this.restockItem.id,
          payload: payload,
        });
        this.restockLoading = false;
        this.restockDialog = false;
        this.restockPriceDialog = false;
        this.$alert({
          title: "Success",
          message:
            "Stock added successfully!" +
            (updatePrice ? " Selling price updated." : ""),
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.restockLoading = false;
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async previewCategoryAdj() {
      this.categoryAdjPreviewLoading = true;
      try {
        this.categoryAdjPreview = await this.category_price_adjustment_preview({
          item_type_id: this.categoryAdjForm.item_type_id,
          adjustment_type: this.categoryAdjForm.adjustment_type,
          adjustment_value: this.categoryAdjForm.adjustment_value,
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.categoryAdjPreviewLoading = false;
    },
    async applyCategoryAdj() {
      const typeName = this.item_types.find(
        (t) => t.id === this.categoryAdjForm.item_type_id
      );
      let confirmed = await this.$confirm({
        title: "Price Adjustment",
        message: `Apply price adjustment to ${
          this.categoryAdjPreview.count
        } item(s) in category "${
          typeName ? typeName.item_type : ""
        }"?<br><br>This action will update all selling prices and log each change.`,
        color: "orange",
        icon: "mdi-alert",
      });
      if (!confirmed) return;

      this.categoryAdjApplyLoading = true;
      try {
        const result = await this.category_price_adjustment({
          item_type_id: this.categoryAdjForm.item_type_id,
          adjustment_type: this.categoryAdjForm.adjustment_type,
          adjustment_value: this.categoryAdjForm.adjustment_value,
          reason: this.categoryAdjForm.reason,
        });
        this.$alert({
          title: "Success",
          message: `Successfully updated ${result.count} item(s).`,
          color: "success",
          icon: "mdi-check-circle",
        });
        this.closeCategoryAdjDialog();
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.categoryAdjApplyLoading = false;
    },
    closeCategoryAdjDialog() {
      this.categoryAdjDialog = false;
      this.categoryAdjPreview = null;
      this.categoryAdjForm = {
        item_type_id: "",
        adjustment_type: "",
        adjustment_value: "",
        reason: "",
      };
    },
    closeBulkReorderDialog() {
      this.bulk_reorder_dialog = false;
      this.bulk_reorder_point = 0;
      this.bulk_min_stock_level = 0;
    },
    saveBulkReorderPoints() {
      this.bulk_saving = true;
      const payload = {
        items: this.selected_items.map((item) => ({
          item_id: item.id,
          reorder_point: this.bulk_reorder_point,
          min_stock_level: this.bulk_min_stock_level,
        })),
      };
      this.bulk_update_reorder_points(payload)
        .then(() => {
          this.selected_items.forEach((item) => {
            item.reorder_point = this.bulk_reorder_point;
            item.min_stock_level = this.bulk_min_stock_level;
          });
          this.$alert({
            title: "Success",
            message:
              "Successfully updated reorder point and min stock level for " +
              this.selected_items.length +
              " item(s)",
            color: "success",
            icon: "mdi-check-circle",
          });
          this.selected_items = [];
          this.closeBulkReorderDialog();
        })
        .catch(() => {
          this.$alert({
            title: "Error",
            message: "Failed to update. Please try again.",
            color: "error",
            icon: "mdi-alert-circle",
          });
        })
        .finally(() => {
          this.bulk_saving = false;
        });
    },
    refresh() {
      this.loading = true;

      this.get_items().then(() => {
        this.loading = false;
      });
    },
  },
  async created() {
    if (this.branches.length <= 0) await this.get_branches();
    if (this.items.length <= 0) await this.get_items("not_all");
    if (this.item_types.length <= 0) await this.get_item_types();
    if (this.suppliers.length <= 0) await this.get_suppliers();
    this.ready = true;
  },
  components: {
    AddItemModal,
    EditItemModal: () => import("../components/Item/editItemModal.vue"),
    PrintBatchItemModal,
    ItemTypesModal,
    AllItemModal: () => import("../components/Item/allItemModal.vue"),
    ItemProfileModal: () => import("../components/Item/itemProfileModal.vue"),
    ItemTransferModal,
    ConsumeItemsModal,
    AddBatchImportModal,
    SerialNumbersInput,
  },
};
</script>

<style lang="scss" scoped></style>
